import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  linkedSignal,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { finalize, forkJoin, map, Observable, of } from 'rxjs';
import { addDays, format, isSameDay, set, startOfDay, startOfWeek } from 'date-fns';
import { ButtonModule } from '@openng/optimus-ui/button';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { FloatLabelModule } from '@openng/optimus-ui/floatlabel';
import { MultiSelectModule } from '@openng/optimus-ui/multiselect';
import { SelectModule } from '@openng/optimus-ui/select';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { TagModule } from '@openng/optimus-ui/tag';
import { TextareaModule } from '@openng/optimus-ui/textarea';

import { DealLookup, DropdownData, GenericDropdownComponent, LookupsService, rxResourceFixed } from '@zayan-erp/shared';
import { AddressFieldConfig, AddressesPrimengComponent, AddressPrimengValue } from '@zayan-erp/home-building';
import {
  DSM_APPOINTMENT_TYPE_LABELS,
  DsmAppointment,
  DsmAppointmentAgendaKind,
  DsmAppointmentAgendaRow,
  DsmAppointmentBooking,
  DsmAppointmentDeal,
  DsmAppointmentLocation,
  DsmAppointmentPrefill,
  DsmAppointmentStatus,
  DsmAppointmentType,
  DsmAppointmentWeekDay
} from '../../models/dsm-appointment.models';
import { DsmAppointmentDealScenario } from '../../models/dsm-appointment-scenario.models';
import { DsmApsStatus } from '../../models/dsm-aps.models';
import { DsmDecorConsultant, DsmDecorConsultantLookup } from '../../models/dsm-decor-consultant.models';
import { DsmAppointmentScenariosService } from '../../services/dsm-appointment-scenarios.service';
import { DsmAppointmentsService } from '../../services/dsm-appointments.service';
import { DsmDecorConsultantService } from '../../services/dsm-decor-consultant.service';
import { DsmStateService } from '../../services/dsm-state.service';

interface AgendaClasses {
  row: string;
  time: string;
  text: string;
}

const AGENDA_CLASSES: Record<DsmAppointmentAgendaKind, AgendaClasses> = {
  [DsmAppointmentAgendaKind.Booked]: {
    row: 'bg-surface-50 shadow-outline-surface',
    time: 'text-surface-800',
    text: 'text-surface-600'
  },
  [DsmAppointmentAgendaKind.New]: {
    row: 'bg-surface-0 shadow-accent-amber pl-5',
    time: 'text-primary-500',
    text: 'text-surface-800'
  },
  [DsmAppointmentAgendaKind.Overlap]: {
    row: 'bg-red-soft shadow-accent-red pl-5',
    time: 'text-red-500',
    text: 'text-surface-800'
  }
};

/** Longest appointment the API accepts. */
const MAX_DURATION_MINUTES = 720;
const MAX_NOTES_LENGTH = 2000;

const SLOT_MINUTES = 15;
const MINUTES_PER_DAY = 24 * 60;
const DEFAULT_DURATION_MINUTES = 60;
const TIME_FORMAT = 'h:mm a';

const TYPE_ITEMS: DropdownData[] = [
  DsmAppointmentType.DesignConsultation,
  DsmAppointmentType.OptionsSelection,
  DsmAppointmentType.Contract
].map(value => ({ id: String(value), firstLabel: DSM_APPOINTMENT_TYPE_LABELS[value] }));

const START_MINUTES: number[] = Array.from(
  { length: MINUTES_PER_DAY / SLOT_MINUTES },
  (_, index) => index * SLOT_MINUTES
);

const LOCATION_FIELDS: AddressFieldConfig[] = [
  { field: 'addressLine1', required: true, visible: true, colSpan: 12 },
  { field: 'addressLine2', required: false, visible: true, colSpan: 12 },
  { field: 'cityId', required: true, visible: true, colSpan: 12 },
  { field: 'suite', required: false, visible: true, colSpan: 6 },
  { field: 'postalCode', required: false, visible: true, colSpan: 6 }
];

/** The moment `minutes` past midnight on `day`, in the browser's time zone. */
function atMinutes(day: Date, minutes: number): Date {
  return set(day, { hours: Math.floor(minutes / 60), minutes: minutes % 60, seconds: 0, milliseconds: 0 });
}

function minutesOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

function toMinutesItem(day: Date, minutes: number): DropdownData {
  return { id: String(minutes), firstLabel: format(atMinutes(day, minutes), TIME_FORMAT) };
}

function toNumberOrNull(id: string | null | undefined): number | null {
  return id === null || id === undefined || id === '' ? null : Number(id);
}

function toDeal(deal: DealLookup): DsmAppointmentDeal {
  return {
    dealId: deal.dealId,
    lotId: deal.lotId,
    lotNo: deal.lotNo,
    projectId: deal.projectId,
    projectName: deal.projectName,
    buyerName: deal.prospectName,
    modelName: deal.modelName,
    elevationCode: deal.elevationCode
  };
}

function toPrefill(appointment: DsmAppointment, isEdit: boolean): DsmAppointmentPrefill {
  const start = new Date(appointment.startDateTime);
  // Only a reschedule keeps the original time, and only while it is still ahead.
  const keepsTime = isEdit && start >= new Date();

  return {
    deal: {
      dealId: appointment.dealId,
      lotId: appointment.lotId,
      lotNo: appointment.lotNo,
      projectId: appointment.projectId,
      projectName: appointment.projectName,
      buyerName: appointment.buyerName,
      modelName: appointment.modelName,
      elevationCode: appointment.elevationCode
    },
    appointmentType: appointment.appointmentType,
    stepIds: isEdit || appointment.status === DsmAppointmentStatus.NoShow ? appointment.stepIds : [],
    assignedUserId: appointment.assignedUserId,
    locationId: appointment.locationId,
    notes: isEdit ? appointment.notes : null,
    start: keepsTime ? start : null,
    end: keepsTime ? new Date(appointment.endDateTime) : null
  };
}

function toLocationLabel(location: DsmAppointmentLocation): string {
  const street = [location.addressLine1, location.suite ? `Suite ${location.suite}` : null].filter(Boolean).join(', ');

  return [street, location.cityName, location.postalCode].filter(Boolean).join(' · ');
}

/**
 * Books, reschedules or rebooks a DSM appointment. Router-agnostic so any page can host it in its own
 * dialog: pass `appointmentId` to reschedule, `sourceAppointmentId` to book from an earlier appointment.
 */
@Component({
  selector: 'dsm-appointment-form',
  imports: [
    DatePipe,
    UpperCasePipe,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    FloatLabelModule,
    GenericDropdownComponent,
    MultiSelectModule,
    SelectModule,
    SkeletonModule,
    TagModule,
    TextareaModule,
    AddressesPrimengComponent
  ],
  templateUrl: './dsm-appointment-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DsmAppointmentFormComponent {
  readonly #appointmentsService = inject(DsmAppointmentsService);
  readonly #lookupsService = inject(LookupsService);
  readonly #scenariosService = inject(DsmAppointmentScenariosService);
  readonly #decorConsultantService = inject(DsmDecorConsultantService);
  readonly #dsmState = inject(DsmStateService);
  readonly #destroyRef = inject(DestroyRef);

  /** The appointment to reschedule. Empty when booking a new one. */
  readonly appointmentId = input<string | null>(null);
  /** An earlier appointment whose deal, steps and consultant a new booking starts from. */
  readonly sourceAppointmentId = input<string | null>(null);

  readonly saved = output<DsmAppointment>();
  readonly cancelled = output<void>();

  protected readonly typeItems = TYPE_ITEMS;
  protected readonly agendaKind = DsmAppointmentAgendaKind;
  protected readonly agendaClasses = AGENDA_CLASSES;
  protected readonly locationFields = LOCATION_FIELDS;
  protected readonly maxNotesLength = MAX_NOTES_LENGTH;

  readonly #today = startOfDay(new Date());

  readonly isEdit = computed(() => !!this.appointmentId());

  readonly #loadId = computed(() => this.appointmentId() ?? this.sourceAppointmentId());

  readonly #appointmentResource = rxResourceFixed({
    params: () => ({ appointmentId: this.#loadId() }),
    defaultValue: null as DsmAppointment | null,
    stream: ({ params }) =>
      params.appointmentId ? this.#appointmentsService.readAppointment(params.appointmentId) : of(null)
  });

  readonly #loaded = computed(() => {
    const appointment = this.#appointmentResource.value();

    return appointment && appointment.id === this.#loadId() ? appointment : null;
  });

  protected readonly loadFailed = computed(() => !!this.#appointmentResource.error());
  protected readonly isLoadingAppointment = computed(() => !!this.#loadId() && !this.#loaded() && !this.loadFailed());

  readonly #prefill = computed(() => {
    const appointment = this.#loaded();

    return appointment ? toPrefill(appointment, this.isEdit()) : null;
  });

  readonly #isRebook = computed(() => {
    const status = this.#loaded()?.status;

    return this.isEdit() ? status === DsmAppointmentStatus.ReschedulingNeeded : status === DsmAppointmentStatus.NoShow;
  });

  /** Title for the hosting dialog. */
  readonly header = computed(() =>
    this.#isRebook() ? 'Rebook Appointment' : this.isEdit() ? 'Reschedule Appointment' : 'New Appointment'
  );

  protected readonly submitLabel = computed(() =>
    this.#isRebook() ? 'Rebook Appointment' : this.isEdit() ? 'Save Changes' : 'Create Appointment'
  );

  readonly #dealsResource = rxResourceFixed({
    params: () => ({ salesOfficeId: this.#dsmState.selectedSalesOfficeId() }),
    defaultValue: [] as DealLookup[],
    stream: ({ params }) =>
      this.#lookupsService.readDealLookups({
        statusIds: [DsmApsStatus.UnderContract],
        salesOfficeId: params.salesOfficeId
      })
  });

  protected readonly isLoadingDeals = computed(() => this.#dealsResource.isLoading());

  protected readonly dealOptions = computed<DsmAppointmentDeal[]>(() => {
    const deals = this.#dealsResource
      .value()
      .map(toDeal)
      .sort((a, b) => a.buyerName.localeCompare(b.buyerName));
    const carried = this.#prefill()?.deal;

    // A rescheduled deal may no longer be Under Contract, but must stay selectable.
    return carried && !deals.some(deal => deal.dealId === carried.dealId) ? [carried, ...deals] : deals;
  });

  protected readonly dealId = linkedSignal<string | null>(() => this.#prefill()?.deal.dealId ?? null);

  protected readonly deal = computed(() => this.dealOptions().find(deal => deal.dealId === this.dealId()) ?? null);

  readonly #dealId = computed(() => this.deal()?.dealId ?? null);
  readonly #projectId = computed(() => this.deal()?.projectId ?? null);
  readonly #lotId = computed(() => this.deal()?.lotId ?? null);

  protected readonly appointmentTypeId = linkedSignal<string | null>(() => {
    const type = this.#prefill()?.appointmentType ?? DsmAppointmentType.OptionsSelection;

    return String(type);
  });

  readonly #appointmentType = computed(() => toNumberOrNull(this.appointmentTypeId()) as DsmAppointmentType | null);

  readonly #scenariosResource = rxResourceFixed({
    params: () => ({ dealId: this.#dealId() }),
    defaultValue: [] as DsmAppointmentDealScenario[],
    stream: ({ params }) =>
      params.dealId ? this.#scenariosService.readScenariosByDeal(params.dealId) : of([] as DsmAppointmentDealScenario[])
  });

  protected readonly isLoadingSteps = computed(() => this.#scenariosResource.isLoading());

  /** Option groups for the steps multiselect: one group per scenario. */
  protected readonly stepGroups = computed(() =>
    this.#scenariosResource.value().map(scenario => ({
      label: scenario.name,
      items: [...scenario.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(step => ({ label: step.name, value: step.id }))
    }))
  );

  protected readonly stepIds = linkedSignal<string[]>(() => {
    const prefill = this.#prefill();

    return prefill && prefill.deal.dealId === this.#dealId() ? prefill.stepIds : [];
  });

  readonly #consultantsResource = rxResourceFixed({
    params: () => ({ projectId: this.#projectId() }),
    defaultValue: [] as DsmDecorConsultantLookup[],
    stream: ({ params }) =>
      params.projectId
        ? this.#decorConsultantService.getDecorConsultants([params.projectId])
        : of([] as DsmDecorConsultantLookup[])
  });

  protected readonly consultantItems = computed<DropdownData[]>(() =>
    this.#consultantsResource.value().map(consultant => ({ id: consultant.userId, firstLabel: consultant.fullName }))
  );

  protected readonly isLoadingConsultants = computed(() => this.#consultantsResource.isLoading());

  readonly #lotConsultantResource = rxResourceFixed({
    params: () => ({ lotId: this.#prefill() ? null : this.#lotId() }),
    defaultValue: null as DsmDecorConsultant | null,
    stream: ({ params }) => (params.lotId ? this.#decorConsultantService.readLotConsultant(params.lotId) : of(null))
  });

  /** The prefilled consultant, else the lot's assigned consultant when they can be booked on this project. */
  readonly #defaultConsultantId = computed<string | null>(() => {
    const prefill = this.#prefill();
    if (prefill && prefill.deal.dealId === this.#dealId()) return prefill.assignedUserId;

    const lotConsultantId = this.#lotConsultantResource.value()?.id;
    const isBookable = this.#consultantsResource.value().some(consultant => consultant.userId === lotConsultantId);

    return isBookable && lotConsultantId ? lotConsultantId : null;
  });

  protected readonly consultantId = linkedSignal<string | null>(() => this.#defaultConsultantId());

  /** Empty while the consultant list is still loading. */
  protected readonly consultantName = computed(
    () => this.#consultantsResource.value().find(item => item.userId === this.consultantId())?.fullName
  );

  protected readonly consultantFirstName = computed(() => this.consultantName()?.split(' ')[0]);

  protected readonly consultantInitials = computed(() =>
    (this.consultantName() ?? '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map(part => part[0].toUpperCase())
      .join('')
  );

  readonly #locationsResource = rxResourceFixed({
    params: () => ({}),
    defaultValue: [] as DsmAppointmentLocation[],
    stream: () => this.#appointmentsService.readLocations()
  });

  protected readonly locationItems = computed<DropdownData[]>(() =>
    this.#locationsResource.value().map(location => ({ id: location.id, firstLabel: toLocationLabel(location) }))
  );

  protected readonly isLoadingLocations = computed(() => this.#locationsResource.isLoading());

  protected readonly locationId = linkedSignal<string | null>(() => this.#prefill()?.locationId ?? null);

  protected readonly day = linkedSignal<Date>(() => {
    const start = this.#prefill()?.start;

    return start ? startOfDay(start) : this.#today;
  });

  protected readonly weekStart = linkedSignal<Date>(() => startOfWeek(this.day()));
  protected readonly weekEnd = computed(() => addDays(this.weekStart(), 6));

  protected readonly isPrevWeekDisabled = computed(() => this.weekStart() <= startOfWeek(this.#today));

  protected readonly startItems = computed<DropdownData[]>(() => {
    const day = this.day();
    const now = new Date();
    const earliest = isSameDay(day, now) ? minutesOfDay(now) : 0;

    return START_MINUTES.filter(minutes => minutes >= earliest).map(minutes => toMinutesItem(day, minutes));
  });

  readonly #weekResource = rxResourceFixed({
    params: () => ({ consultantId: this.consultantId(), dealId: this.#dealId(), weekStart: this.weekStart() }),
    defaultValue: [] as DsmAppointment[],
    stream: ({ params }) => {
      if (!params.consultantId && !params.dealId) return of([] as DsmAppointment[]);

      const range = { from: params.weekStart.toISOString(), to: addDays(params.weekStart, 7).toISOString() };
      const requests: Observable<DsmAppointment[]>[] = [];

      if (params.consultantId) {
        requests.push(this.#appointmentsService.readAppointments({ ...range, consultantId: params.consultantId }));
      }
      if (params.dealId) requests.push(this.#appointmentsService.readAppointments({ ...range, dealId: params.dealId }));

      return forkJoin(requests).pipe(
        map(lists => [...new Map(lists.flat().map(appointment => [appointment.id, appointment])).values()])
      );
    }
  });

  protected readonly isLoadingWeek = computed(() => this.#weekResource.isLoading());

  /** The consultant's and the lot's other appointments this week: the ones a new booking must not overlap. */
  readonly #bookings = computed<DsmAppointmentBooking[]>(() => {
    const editingId = this.appointmentId();

    return this.#weekResource
      .value()
      .filter(appointment => appointment.id !== editingId)
      .map(appointment => ({
        appointment,
        start: new Date(appointment.startDateTime),
        end: new Date(appointment.endDateTime)
      }))
      .sort((a, b) => a.start.getTime() - b.start.getTime());
  });

  protected readonly weekDays = computed<DsmAppointmentWeekDay[]>(() => {
    const selected = this.day();
    const bookings = this.#bookings();

    return Array.from({ length: 7 }, (_, index) => {
      const date = addDays(this.weekStart(), index);

      return {
        date,
        isPast: date < this.#today,
        isSelected: isSameDay(date, selected),
        bookedCount: bookings.filter(booking => isSameDay(booking.start, date)).length
      };
    });
  });

  protected readonly startId = linkedSignal<string | null>(() => {
    const start = this.#prefill()?.start;

    return start ? String(minutesOfDay(start)) : null;
  });

  readonly #startMinutes = computed(() => toNumberOrNull(this.startId()));

  /** Follows the start time, keeping the length the user last picked (an hour by default). */
  protected readonly endId = linkedSignal<
    { start: number | null; prefill: DsmAppointmentPrefill | null },
    string | null
  >({
    source: () => ({ start: this.#startMinutes(), prefill: this.#prefill() }),
    computation: ({ start, prefill }, previous) => {
      if (start === null) return null;
      if (prefill?.start && prefill.end && previous?.source.prefill !== prefill) {
        const length = Math.round((prefill.end.getTime() - prefill.start.getTime()) / 60_000);

        return String(Math.min(start + length, MINUTES_PER_DAY));
      }

      const previousStart = previous?.source.start ?? null;
      const previousEnd = toNumberOrNull(previous?.value);
      const length =
        previousStart !== null && previousEnd !== null ? previousEnd - previousStart : DEFAULT_DURATION_MINUTES;

      return String(Math.min(start + Math.max(length, SLOT_MINUTES), MINUTES_PER_DAY));
    }
  });

  readonly #endMinutes = computed(() => toNumberOrNull(this.endId()));

  protected readonly endItems = computed<DropdownData[]>(() => {
    const start = this.#startMinutes();
    if (start === null) return [];

    const day = this.day();
    const last = Math.min(start + MAX_DURATION_MINUTES, MINUTES_PER_DAY);
    const items: DropdownData[] = [];

    for (let minutes = start + SLOT_MINUTES; minutes <= last; minutes += SLOT_MINUTES) {
      items.push(toMinutesItem(day, minutes));
    }

    return items;
  });

  /** The picked slot, or empty until both ends are set. */
  protected readonly slot = computed(() => {
    const start = this.#startMinutes();
    const end = this.#endMinutes();
    if (start === null || end === null || end <= start) return null;

    const day = this.day();

    return { start: atMinutes(day, start), end: atMinutes(day, end), durationMinutes: end - start };
  });

  readonly #dayBookings = computed(() => {
    const day = this.day();

    return this.#bookings().filter(booking => isSameDay(booking.start, day));
  });

  /** Only the consultant's and the lot's appointments are loaded, so any overlap is a real clash. */
  protected readonly clash = computed(() => {
    const slot = this.slot();
    if (!slot) return null;

    return this.#dayBookings().find(booking => slot.start < booking.end && slot.end > booking.start) ?? null;
  });

  protected readonly isConsultantClash = computed(
    () => this.clash()?.appointment.assignedUserId === this.consultantId()
  );

  protected readonly isPastSlot = computed(() => {
    const slot = this.slot();

    return !!slot && slot.start < new Date();
  });

  protected readonly bookedCount = computed(() => this.#dayBookings().length);

  protected readonly agendaRows = computed<DsmAppointmentAgendaRow[]>(() => {
    const rows: DsmAppointmentAgendaRow[] = this.#dayBookings().map(booking => ({
      key: booking.appointment.id,
      kind: DsmAppointmentAgendaKind.Booked,
      start: booking.start,
      end: booking.end,
      appointment: booking.appointment
    }));
    const slot = this.slot();

    if (slot) {
      rows.push({
        key: 'new',
        kind: this.clash() ? DsmAppointmentAgendaKind.Overlap : DsmAppointmentAgendaKind.New,
        start: slot.start,
        end: slot.end,
        appointment: null
      });
    }

    return rows.sort((a, b) => a.start.getTime() - b.start.getTime());
  });

  protected readonly notes = linkedSignal<string>(() => this.#prefill()?.notes ?? '');
  protected readonly notesOpened = signal(false);
  protected readonly isNotesOpen = computed(() => this.notesOpened() || !!this.notes());

  protected readonly isSaving = signal(false);

  protected readonly canSave = computed(
    () =>
      !!this.deal() &&
      !!this.consultantId() &&
      !!this.locationId() &&
      !!this.slot() &&
      !this.isPastSlot() &&
      !this.clash() &&
      !this.isSaving()
  );

  protected readonly locationDialogVisible = signal(false);
  protected readonly locationSubmitted = signal(false);
  protected readonly isSavingLocation = signal(false);
  protected readonly locationControl = new FormControl<AddressPrimengValue | null>(null);

  protected pickDay(day: DsmAppointmentWeekDay): void {
    if (!day.isPast) this.day.set(day.date);
  }

  protected shiftWeek(weeks: number): void {
    this.weekStart.update(start => addDays(start, weeks * 7));
  }

  protected openLocationDialog(): void {
    this.locationControl.reset(null);
    this.locationSubmitted.set(false);
    this.locationDialogVisible.set(true);
  }

  protected saveLocation(address: AddressesPrimengComponent): void {
    this.locationSubmitted.set(true);
    if (!address.isValid()) return;

    const value = address.getFormValue();

    this.isSavingLocation.set(true);
    this.#appointmentsService
      .createLocation({
        addressLine1: value.addressLine1?.trim() ?? '',
        addressLine2: value.addressLine2?.trim() ?? '',
        suite: value.suite?.trim() ?? '',
        cityId: value.cityId ?? '',
        postalCode: value.postalCode?.trim() ?? ''
      })
      .pipe(
        finalize(() => this.isSavingLocation.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(location => {
        this.locationDialogVisible.set(false);
        this.#locationsResource.reload();
        if (location?.id) this.locationId.set(location.id);
      });
  }

  protected save(): void {
    const deal = this.deal();
    const slot = this.slot();
    const appointmentType = this.#appointmentType() ?? DsmAppointmentType.OptionsSelection;
    const consultantId = this.consultantId();
    const locationId = this.locationId();
    if (!this.canSave() || !deal || !slot || !consultantId || !locationId) return;

    const payload = {
      startDateTime: slot.start.toISOString(),
      durationMinutes: slot.durationMinutes,
      appointmentType,
      locationId,
      assignedUserId: consultantId,
      stepIds: this.stepIds(),
      notes: this.notes().trim() || null
    };

    const appointmentId = this.appointmentId();
    const rebookedFromId = this.#isRebook() ? this.sourceAppointmentId() : null;
    const request = appointmentId
      ? this.#appointmentsService.updateAppointment(appointmentId, payload)
      : this.#appointmentsService.createAppointment({
          ...payload,
          dealId: deal.dealId,
          ...(rebookedFromId ? { rebookedFromId } : {})
        });

    this.isSaving.set(true);
    request
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(appointment => this.saved.emit(appointment));
  }

  protected retryLoad(): void {
    this.#appointmentResource.reload();
  }
}
